import React, { useState } from 'react';
import {
  Typography, Card, Table, Tag, Button, Space, Select, Input, Row, Col, Statistic,
  Tooltip, Modal, Descriptions,
} from 'antd';
import {
  ReloadOutlined, EyeOutlined, SearchOutlined,
  ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, DollarOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { refundApi } from '../services/api';
import { formatBeijingTime } from '../utils/dateUtils';

const { Title, Text } = Typography;

const statusMap: Record<string, { text: string; color: string; icon?: React.ReactNode }> = {
  pending: { text: '待处理', color: 'orange', icon: <ClockCircleOutlined /> },
  processing: { text: '处理中', color: 'processing', icon: <ClockCircleOutlined /> },
  completed: { text: '已完成', color: 'success', icon: <CheckCircleOutlined /> },
  failed: { text: '失败', color: 'error', icon: <CloseCircleOutlined /> },
};

const RefundManagement: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [detailModal, setDetailModal] = useState<{ visible: boolean; record: any | null }>({ visible: false, record: null });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['refunds', statusFilter, search, currentPage, pageSize],
    queryFn: async () => {
      const params: any = { page: currentPage, pageSize };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      return await refundApi.getRefunds(params) as any;
    },
  });

  const refunds = data?.items || [];
  const total = data?.total || 0;

  // 统计
  const completedCount = refunds.filter((r: any) => r.status === 'completed').length;
  const pendingCount = refunds.filter((r: any) => r.status === 'pending' || r.status === 'processing').length;
  const totalAmount = refunds.reduce((sum: number, r: any) => sum + (r.amount || 0), 0);

  const columns = [
    {
      title: '退款单号', dataIndex: 'refund_id', key: 'refund_id', width: 200,
      render: (text: string) => <Text copyable={{ text }} style={{ fontSize: 12 }}>{text}</Text>,
    },
    {
      title: '用户', key: 'user', width: 120,
      render: (_: any, record: any) => (
        <Tooltip title={`ID: ${record.user_id || '-'}`}>
          <Text>{record.username ? `@${record.username}` : record.first_name || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '商户名', key: 'merchant_name', width: 140,
      render: (_: any, record: any) => (
        <Tooltip title={`bot_id: ${record.bot_id || '-'}`}>
          <Text>{record.bot_name || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '机器人用户名', key: 'merchant_username', width: 150,
      render: (_: any, record: any) => (
        record.bot_username ? <Text copyable>@{record.bot_username}</Text> : <Text>-</Text>
      ),
    },
    {
      title: '退款金额', dataIndex: 'amount', key: 'amount', width: 120,
      render: (amount: number) => <Text strong style={{ color: '#ff4d4f' }}>-{amount?.toFixed(2)}</Text>,
    },
    {
      title: '原支付金额', dataIndex: 'original_payment_amount', key: 'original_payment_amount', width: 120,
      render: (amount: number) => amount ? <Text>{amount.toFixed(2)}</Text> : '-',
    },
    {
      title: '支付方式', dataIndex: 'original_payment_method', key: 'original_payment_method', width: 100,
      render: (method: string) => {
        const m: Record<string, string> = { epay: '易支付', usdt: 'USDT', alipay: '支付宝', wechat: '微信', wechat_1: '微信支付', wechat_2: '微信支付2', alipay_1: '支付宝1', alipay_2: '支付宝2', alipay_3: '支付宝3' };
        return m[method] || method || '-';
      },
    },
    {
      title: '退款原因', dataIndex: 'reason', key: 'reason', width: 160, ellipsis: true,
      render: (text: string) => <Tooltip title={text}><Text>{text || '-'}</Text></Tooltip>,
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (status: string) => {
        const config = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
      },
    },
    {
      title: '申请时间', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: (date: string) => date ? formatBeijingTime(date) : '-',
    },
    {
      title: '完成时间', dataIndex: 'completed_at', key: 'completed_at', width: 160,
      render: (date: string) => date ? formatBeijingTime(date) : '-',
    },
    {
      title: '操作', key: 'actions', width: 80, fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Tooltip title="查看详情">
          <Button type="text" icon={<EyeOutlined />} onClick={() => setDetailModal({ visible: true, record })} />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title level={2} className="mb-2">退款记录</Title>
          <Text type="secondary">查看所有退款申请和处理状态</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>刷新</Button>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="总退款记录" value={total} prefix={<DollarOutlined style={{ color: '#722ed1' }} />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="待处理/处理中" value={pendingCount}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="已完成" value={completedCount}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="当页退款总额" value={totalAmount} precision={2}
              prefix="¥" valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>

      {/* 筛选和表格 */}
      <Card size="small">
        <div className="mb-4">
          <Space wrap>
            <span>状态：</span>
            <Select style={{ width: 130 }} value={statusFilter} allowClear placeholder="全部"
              onChange={(v) => { setStatusFilter(v || ''); setCurrentPage(1); }}>
              <Select.Option value="pending">待处理</Select.Option>
              <Select.Option value="processing">处理中</Select.Option>
              <Select.Option value="completed">已完成</Select.Option>
              <Select.Option value="failed">失败</Select.Option>
            </Select>
            <Input.Search placeholder="搜索退款单号/支付ID" allowClear style={{ width: 240 }}
              prefix={<SearchOutlined />}
              onSearch={(v) => { setSearch(v); setCurrentPage(1); }} />
          </Space>
        </div>
        <Table columns={columns} dataSource={refunds} rowKey="id" loading={isLoading}
          scroll={{ x: 1300 }} size="small"
          pagination={{
            current: currentPage, pageSize, total,
            showSizeChanger: false, showTotal: (t) => `共 ${t} 条记录`,
            onChange: (page) => setCurrentPage(page),
          }} />
      </Card>

      {/* 详情弹窗 */}
      <Modal title="退款详情" open={detailModal.visible} footer={null} width={640}
        onCancel={() => setDetailModal({ visible: false, record: null })}>
        {detailModal.record && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="退款单号" span={2}>
              <Text copyable>{detailModal.record.refund_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="用户">
              {detailModal.record.username ? `@${detailModal.record.username}` : detailModal.record.first_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={statusMap[detailModal.record.status]?.color} icon={statusMap[detailModal.record.status]?.icon}>
                {statusMap[detailModal.record.status]?.text || detailModal.record.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="退款金额">
              <Text strong style={{ color: '#ff4d4f' }}>-{detailModal.record.amount?.toFixed(2)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="原支付金额">
              {detailModal.record.original_payment_amount?.toFixed(2) || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="支付方式">
              {detailModal.record.original_payment_method || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="退款原因" span={2}>
              {detailModal.record.reason || '-'}
            </Descriptions.Item>
            {detailModal.record.refund_address && (
              <Descriptions.Item label="退款地址" span={2}>
                <Text copyable>{detailModal.record.refund_address}</Text>
              </Descriptions.Item>
            )}
            {detailModal.record.refund_tx_hash && (
              <Descriptions.Item label="退款交易哈希" span={2}>
                <Text copyable>{detailModal.record.refund_tx_hash}</Text>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="申请时间" span={2}>
              {formatBeijingTime(detailModal.record.created_at)}
            </Descriptions.Item>
            {detailModal.record.completed_at && (
              <Descriptions.Item label="完成时间" span={2}>
                {formatBeijingTime(detailModal.record.completed_at)}
              </Descriptions.Item>
            )}
            {detailModal.record.bot_name && (
              <Descriptions.Item label="关联机器人" span={2}>
                {detailModal.record.bot_name} (@{detailModal.record.bot_username})
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default RefundManagement;
